import { useEffect, useRef } from 'react';
import { Box } from '@chakra-ui/react';
import { Text } from '@kula-ai/components';
import { useFormikContext } from 'formik';
import { hasUsableValue } from '@/api/customFields/formulaWireUnits';
import { colors } from '@/theme/foundations/colors';
import { toControlValue } from './calculatedControlValue';
import type { ControlOption } from './calculatedControlValue';
import { describeDiagnostic } from '@/utils/formula/formulaDiagnostics';
import { useCalculatedValue } from './useCalculatedValue';
import type { CalculatedFieldState } from './calculatedField.interface';

// One entry of the rule's `blocked_by`. A `field_id` means the blocker is a row
// on this form, so the recruiter can fill it; without one it lives on another
// object — a requisition or job field — and no amount of filling in here will
// help (Gokul, CORE-5475).
export interface Blocker {
  label?: string;
  field_id?: string | number | null;
}

export interface CalculatedFieldSyncProps {
  // The form field the result is written to, so the field's own control shows
  // it — a calculated Yes/No is the Yes/No control, not a box of text.
  name: string;
  formula: string;
  fieldNameOf: (token: string) => string;
  // The backend's verdict on the saved rule. Omitted — nothing sends it yet —
  // means show whatever the server computes now.
  state?: CalculatedFieldState;
  // The fields a 'blocked' rule is still waiting on, named for the recruiter.
  blockedBy?: Blocker[];
  // Unlocked, the recruiter may type over the result, so it is theirs to keep.
  locked?: boolean;
  subjectType?: string;
  kind?: string;
  fieldId?: string | number;
  // A select's own options: the formula answers with their names, the control
  // holds their ids.
  options?: ControlOption[];
  multi?: boolean;
}

const quotedList = (names: string[]) =>
  names.length < 2
    ? `"${names[0]}"`
    : `${names
        .slice(0, -1)
        .map((name) => `"${name}"`)
        .join(', ')} and "${names[names.length - 1]}"`;

const blockedMessage = (names: string[]) =>
  `Fill in ${quotedList(names)} to see this value`;

const notHere = (names: string[]) =>
  `${quotedList(names)}, which ${names.length > 1 ? 'are' : 'is'} not on this offer form`;

// Nothing on this form will produce an off-form blocker, so the recruiter is
// told where the value has to come from rather than asked to fill it in — and
// a mixed list says both, or filling the fillable half leads nowhere.
const waitingMessage = (fillable: string[], offForm: string[]) => {
  if (fillable.length === 0) return `This value needs ${notHere(offForm)}`;
  if (offForm.length === 0) return blockedMessage(fillable);
  return `${blockedMessage(fillable)} — it also needs ${notHere(offForm)}`;
};

// The one diagnostic a recruiter can clear themselves: the engine says this
// when it has no value to work with. Every other code is the formula's own
// fault and no amount of filling in will help.
const isMissingInput = (diagnostic?: { code?: string } | null) =>
  diagnostic?.code === 'err_formula_not_computable';

const Refusal = ({ children }: { children: string }) => (
  // kula Text drops data-* attributes, so the hook sits on the wrapper.
  <Box mt="1" data-test-id="calculated-blocked">
    <Text variant="small" style={{ color: colors['pastel-1-dark'] }}>
      {children}
    </Text>
  </Box>
);

// Sits under a calculated field: works the value out, puts it where the
// field's own control reads it, and says why when there is none to show.
// It renders no control of its own — the kind's config already does.
export const CalculatedFieldSync = ({
  name,
  formula,
  fieldNameOf,
  state,
  blockedBy,
  locked = true,
  subjectType,
  kind,
  fieldId,
  options,
  multi,
}: CalculatedFieldSyncProps) => {
  const { values, setFieldValue } = useFormikContext<Record<string, unknown>>();
  const {
    value: computed,
    missing,
    diagnostic,
  } = useCalculatedValue({
    formula,
    values,
    fieldNameOf,
    subjectType,
    kind,
    fieldId,
  });

  const named = (blockedBy ?? []).filter(
    (blocker): blocker is Blocker & { label: string } => Boolean(blocker?.label)
  );
  const onForm = named
    .filter(
      (blocker) => blocker.field_id !== null && blocker.field_id !== undefined
    )
    .map((blocker) => blocker.label);
  const offForm = named
    .filter(
      (blocker) => blocker.field_id === null || blocker.field_id === undefined
    )
    .map((blocker) => blocker.label);
  // The server said nothing, so the local engine's own reading of the formula
  // names what it could not read.
  const fillable =
    onForm.length > 0 || offForm.length > 0
      ? onForm
      : [missing[0]?.label].filter((label): label is string => Boolean(label));
  const waitingFor = fillable[0] ?? offForm[0];
  const blocked = state === 'blocked' || (!state && Boolean(waitingFor));
  // The server has just worked this one out, from what the form holds now.
  const live = computed !== null && computed !== undefined;
  // A 'blocked' or 'error' rule can still carry a value from an earlier save,
  // so the state decides whether a SAVED value may be shown at all.
  //
  // It cannot decide that for a live one. `state` is the offer's verdict from
  // its last write, so it is stale the moment anyone types — a field saved
  // blocked stayed blank however the recruiter filled the field it was waiting
  // on, even while evaluate_formula was answering with the right number on
  // every keystroke (CORE-5539). `hidden_by_condition` had the same problem and
  // the same answer (ENG-4212): re-read live, and let the saved flag speak only
  // when there is nothing live to speak for it.
  const usable =
    live ||
    hasUsableValue({
      state: blocked ? 'blocked' : (state ?? 'ok'),
      value: computed,
    });

  const shown = usable
    ? toControlValue(kind, computed, { options, multi })
    : { value: '' };
  // A result the field cannot hold is not written to a locked field: the
  // filler cannot correct it, so an empty box and the reason is all there is.
  // Unlocked, they can, so they are given the value to correct (CORE-5477).
  const next =
    shown.unstorable && locked ? '' : (shown.value ?? String(computed ?? ''));

  // A locked field publishes on every recompute — it is the formula's to own.
  //
  // An unlocked one is seeded once and then left alone: publishing again would
  // overwrite whatever the recruiter had typed, which is the override the
  // field is unlocked to allow in the first place.
  const seededRef = useRef(false);
  useEffect(() => {
    if (locked) {
      if (JSON.stringify(values[name] ?? '') === JSON.stringify(next ?? ''))
        return;
      setFieldValue(name, next);
      return;
    }
    if (seededRef.current || !usable) return;
    seededRef.current = true;
    // Only ever into an empty box. A value already there is the recruiter's —
    // typed now, or overridden on a previous save — and this field is
    // unlocked precisely so that it wins.
    const held = values[name];
    if (held !== '' && held !== null && held !== undefined) return;
    if (JSON.stringify(held ?? '') === JSON.stringify(next ?? '')) return;
    setFieldValue(name, next);
  }, [locked, name, next, usable, setFieldValue, values]);

  // Said either way: locked, it is why the box is empty; unlocked, it is what
  // the recruiter has to correct before the offer will save.
  if (shown.unstorable) return <Refusal>{shown.unstorable}</Refusal>;
  // "This formula cannot be worked out" is what the engine answers when an
  // input is empty, and on its own it is a dead end — the recruiter is the one
  // who can fill that box. Where we know which field is missing, name it
  // instead (CORE-5487).
  //
  // Said on an optional field too. The rule used to be that an optional field
  // simply stays blank, but a diagnostic was rendered regardless of mandatory,
  // so the quieter message was never what happened: the filler got the dead
  // end either way. Naming the field is strictly better than that.
  // A refusal the filler cannot clear comes first: it is the admin's to fix,
  // and telling the recruiter to fill a box would send them nowhere.
  if (diagnostic && !isMissingInput(diagnostic))
    return <Refusal>{describeDiagnostic(diagnostic)}</Refusal>;
  if (!live && waitingFor && (blocked || isMissingInput(diagnostic)))
    return <Refusal>{waitingMessage(fillable, offForm)}</Refusal>;
  // Nothing to work with and nothing to name: the generic line is all there is.
  if (diagnostic) return <Refusal>{describeDiagnostic(diagnostic)}</Refusal>;
  return null;
};
